import React, { useState, useEffect } from 'react';
import { Container, Paper, Typography, TextField, Button, Box, Alert } from '@mui/material';
import { useAuth } from '../context/AuthContext';
import { updateUserProfile } from '../services/userProfileService';
import { updateEmail, updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';

function MyAccount() {
  const { user, userProfile } = useAuth();
  const [eaId, setEaId] = useState('');
  const [email, setEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user && userProfile) {
      setEaId(userProfile.eaId || '');
      setEmail(user.email || '');
    }
  }, [user, userProfile]);

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSuccess('');
    setError('');
    setLoading(true);
    try {
      // Update EA ID in Firestore
      if (eaId !== userProfile.eaId) {
        await updateUserProfile(user.uid, { eaId });
      }
      // Update email in Firebase Auth
      if (email !== user.email) {
        if (!currentPassword) throw new Error('Please enter your current password to change email.');
        const credential = EmailAuthProvider.credential(user.email, currentPassword);
        await reauthenticateWithCredential(user, credential);
        await updateEmail(user, email);
      }
      // Update password in Firebase Auth
      if (newPassword) {
        if (!currentPassword) throw new Error('Please enter your current password to change password.');
        const credential = EmailAuthProvider.credential(user.email, currentPassword);
        await reauthenticateWithCredential(user, credential);
        await updatePassword(user, newPassword);
      }
      setSuccess('Account updated successfully!');
      setNewPassword('');
      setCurrentPassword('');
    } catch (err) {
      setError(err.message || 'Failed to update account.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 8, mb: 8 }}>
      <Paper elevation={6} sx={{ p: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          My Account
        </Typography>
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <form onSubmit={handleUpdate}>
          <TextField
            fullWidth
            label="EA ID"
            value={eaId}
            onChange={e => setEaId(e.target.value)}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            margin="normal"
            type="email"
          />
          <TextField
            fullWidth
            label="Current Password"
            value={currentPassword}
            onChange={e => setCurrentPassword(e.target.value)}
            margin="normal"
            type="password"
            helperText="Required to change email or password."
          />
          <TextField
            fullWidth
            label="New Password"
            value={newPassword}
            onChange={e => setNewPassword(e.target.value)}
            margin="normal"
            type="password"
            helperText="Leave blank to keep current password."
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button type="submit" variant="contained" color="primary" disabled={loading}>
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Container>
  );
}

export default MyAccount; 